/**
 * One project as a row in the register, and the grid both it and the list head
 * are drawn on.
 *
 * Lifted out of `ProjectsList` unchanged. The list file is about to carry its
 * filters in the address bar and, a round later, four more filters on top of
 * that; a row that keeps growing inside it takes the screen file past the point
 * where anybody can find the piece they came for (AGENTS.md §4).
 */
import { Link } from "react-router-dom";
import { CalendarClock } from "lucide-react";
import { cn } from "@wa/ui";
import { useT } from "../../../i18n/I18nProvider";
import type { Project } from "../../../lib/projects";
import { Avatar, StatusPill, fmtDate, fmtNum } from "../../ui";
import { ArchivedBadge } from "./ProjectArchive";
import { ProjectRowMenu } from "./ProjectRowMenu";
import { Progress } from "./Progress";
import { money } from "./lib";
import { statusTone } from "./status";
import { PinnedBadge } from "./ProjectPin";

/**
 * Project, status, deadline, progress, budget, tasks - six tracks plus the
 * actions column, used by both the head and the rows so a new column cannot
 * shift one without the other.
 *
 * It lives with the row rather than with the list because the row is the thing
 * the tracks describe, and the head imports it from here. Two separate track
 * lists drift apart the first time somebody adds a column, and grid says
 * nothing when they do - the figures just move one heading to the left.
 */
export const PROJECT_COLS = "minmax(0,2.2fr) 1fr .95fr 1.05fr .85fr .55fr 44px";

export function ProjectsListRow({
  project,
  onChanged,
  onDelete,
  onArchiveChanged,
  onPinChanged,
}: {
  project: Project;
  onChanged: (next: Project) => void;
  onDelete: () => void;
  /** Straight through to the row menu - see the prop's own note there. */
  onArchiveChanged: () => void;
  /** Also straight through. The row itself does nothing with a pin beyond
   *  wearing its badge, which comes off `project.pinned` and therefore off the
   *  row the list patches. */
  onPinChanged: (pinned: boolean) => void;
}) {
  const t = useT();

  // Late only counts while there is still something to deliver. A project that
  // finished after its deadline is finished, and painting it red forever turns
  // the colour into wallpaper.
  const late =
    !!project.deadline &&
    project.status !== "completed" &&
    project.status !== "cancelled" &&
    new Date(project.deadline) < new Date();

  return (
    // A div, not a link: the row ends in a menu button, and interactive content
    // inside an <a> is invalid HTML and swallows the button's clicks. The name
    // link stretches an ::after over the whole row instead, so the row stays
    // clickable end to end and the button still sits on top of it.
    <div className="crm-list-row relative" style={{ gridTemplateColumns: PROJECT_COLS }}>
      <span className="flex min-w-0 items-center gap-3">
        <Avatar name={project.name} />
        <span className="min-w-0">
          <Link
            to={`/projects/${project.id}`}
            className="crm-prow-n crm-trunc block after:absolute after:inset-0 after:rounded-full"
          >
            {project.name}
          </Link>
          <span className="crm-prow-s crm-trunc block">{project.client?.name ?? "-"}</span>
        </span>
      </span>

      {/* Two pills in one track, and they answer different questions: the
          status is where the WORK stands, the archive mark is whether the
          project is still being offered. The archive one is only ever drawn in
          a view that was asked to show archived rows, so the column is a single
          pill on the register the operator looks at every day. It wraps rather
          than truncates - a badge cut in half says less than no badge at all. */}
      <span className="flex flex-wrap items-center gap-1.5">
        <StatusPill tone={statusTone(project.status)}>{t(`projects.status.${project.status}`)}</StatusPill>
        <ArchivedBadge project={project} />
        {/* Into the status track and not a column of its own: the track is
            already `flex flex-wrap` for the archive mark, so a second badge
            costs nothing - where a seventh grid column would have to be added
            to `PROJECT_COLS`, to the head and to this row at once, or every
            figure shifts one heading left. */}
        <PinnedBadge project={project} />
      </span>

      <span className={cn("crm-tnum flex items-center gap-1.5 text-[12.5px]", late ? "text-red-600" : "crm-mut")}>
        <CalendarClock className="size-3.5 shrink-0" />
        {fmtDate(project.deadline)}
      </span>

      {/* The EFFECTIVE percentage, not the stored one. On a "tasks" project the
          column still carries whatever a person last typed, and printing that
          would put a third number on screen beside the project page and the
          client's portal - the exact split this round exists to end. Only the
          list endpoint resolves it, hence the fallback: a row that arrived some
          other way (the PATCH response written back after a roadmap move) shows
          the stored figure, which is stale rather than blank. */}
      <Progress pct={project.effectiveProgressPct ?? project.progressPct} />

      {/* Budget, which is what was agreed - not what has been spent or billed.
          Those two only exist on the project summary endpoint, one project at a
          time, so a list column for them would be a column of blanks. */}
      <span className="crm-tnum text-[13.25px] font-bold">{money(project.budgetCents)}</span>

      <span className="crm-tnum text-[13px]">{fmtNum(project._count?.tasks ?? 0)}</span>

      <ProjectRowMenu
        project={project}
        onChanged={onChanged}
        onDelete={onDelete}
        onArchiveChanged={onArchiveChanged}
        onPinChanged={onPinChanged}
      />
    </div>
  );
}
